import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Modal } from "PFComponents/modal";
import { Pill } from "PFComponents/pill";
import { Select } from "PFComponents/select";
import api from "PFCore/api";
import { useChatInvalidate } from "PFCore/hooks/queries/chat/use_chat_invalidate";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { fetchProfilesOptions } from "PFCore/services/profile";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

const InviteModal = ({ conversation, participants, handleClose }) => {
  const [items, setItems] = useState([]);
  const { showGrowl } = useGrowlStoreActions();
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation();
  const { invalidateParticipants } = useChatInvalidate();

  const fetchOptions = useCallback(
    ({ searchTerm }) =>
      fetchProfilesOptions({ term: searchTerm }).then((data) => ({
        ...data,
        entries: data.entries
          .filter((item) => currentProfile.id !== item.id)
          .map((item) => {
            const alreadyParticipates = !!participants.find(({ profile }) => profile.id === item.id);

            return {
              id: item.id,
              value: item.text,
              disabled: alreadyParticipates,
              displayElement: (
                <>
                  {alreadyParticipates && <Pill text={t("conversations.participant")} small />}
                  <span>{` ${item.text}`}</span>
                </>
              ),
              original: item
            };
          })
      })),
    [currentProfile.id, participants, t]
  );

  return (
    <Modal
      title={t("conversations.inviteToConversation", { title: conversation?.title || "" })}
      onClose={handleClose}
      labelOK={t("conversations.inviteParticipants", { count: items.length })}
      showOKButton={items.length > 0}
      onOK={() => {
        Promise.all(
          items.map(({ id }) =>
            api({
              url: "chat/participants",
              method: "POST",
              data: { conversationId: conversation.id, profileId: id }
            })
          )
        ).then(() => {
          invalidateParticipants(conversation.id);
          showGrowl({
            message: t("conversations.invitedToConvo", {
              count: items.length,
              invited: items.length === 1 ? items[0].value : items.length
            })
          });
        });
      }}
    >
      <Select
        placeholder={t("conversations.userNamePlaceholder")}
        cacheTime={0}
        multiple={true}
        value={items.map((item) => item.id)}
        onChange={(options) => setItems(options)}
        fetchOptions={fetchOptions}
      />
      {items.length > 0 && (
        <p style={{ marginTop: 30 }}>
          {t("conversations.participantWillReceiveEmail", { count: items.length })}
        </p>
      )}
    </Modal>
  );
};

InviteModal.propTypes = {
  participants: PropTypes.array,
  conversation: PropTypes.shape({ id: PropTypes.number, title: PropTypes.string }),
  handleClose: PropTypes.func
};

export default InviteModal;
